import type { LogEntry, SeverityKind } from '../types';

const asObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const readOtlpAnyValue = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value !== 'object') {
    return value;
  }

  const node = value as Record<string, unknown>;

  if (node.stringValue !== undefined) return node.stringValue;
  if (node.boolValue !== undefined) return node.boolValue;
  if (node.doubleValue !== undefined) return node.doubleValue;
  if (node.intValue !== undefined) return node.intValue;
  if (node.bytesValue !== undefined) return node.bytesValue;

  if (node.arrayValue && typeof node.arrayValue === 'object') {
    const values = asArray((node.arrayValue as Record<string, unknown>).values);
    return values.map((item) => readOtlpAnyValue(item));
  }

  if (node.kvlistValue && typeof node.kvlistValue === 'object') {
    const kvEntries = asArray((node.kvlistValue as Record<string, unknown>).values);
    return kvEntries.reduce<Record<string, unknown>>((acc, entry) => {
      const pair = asObject(entry);
      const key = asString(pair.key);
      if (!key) {
        return acc;
      }
      acc[key] = readOtlpAnyValue(pair.value);
      return acc;
    }, {});
  }

  return value;
};

const parseOtlpAttributes = (value: unknown): Record<string, unknown> => {
  if (Array.isArray(value)) {
    return value.reduce<Record<string, unknown>>((acc, entry) => {
      const pair = asObject(entry);
      const key = asString(pair.key);
      if (!key) {
        return acc;
      }
      acc[key] = readOtlpAnyValue(pair.value);
      return acc;
    }, {});
  }

  return asObject(parseJsonIfString(value));
};

const bodyToText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  const decoded = readOtlpAnyValue(value);
  if (typeof decoded === 'string') {
    return decoded;
  }

  if (decoded === null || decoded === undefined) {
    return 'Log event';
  }

  if (typeof decoded === 'object') {
    try {
      return JSON.stringify(decoded);
    } catch {
      return 'Log event';
    }
  }

  return String(decoded);
};

const toSeverityKind = (severityNumber: number): SeverityKind => {
  if (severityNumber >= 17) {
    return 'error';
  }

  if (severityNumber >= 13) {
    return 'warn';
  }

  return 'info';
};

const severityFromText = (severityText: string | undefined): number | undefined => {
  if (!severityText) {
    return undefined;
  }

  const normalized = severityText.toLowerCase();
  if (normalized.includes('error') || normalized.includes('fatal')) return 17;
  if (normalized.includes('warn')) return 13;
  if (normalized.includes('debug') || normalized.includes('trace')) return 5;
  return 9;
};

const timestampToMillis = (candidate: unknown): number => {
  const numeric = asNumber(candidate);
  if (!numeric) {
    return Date.now();
  }

  if (numeric > 1_000_000_000_000_000) {
    return Math.floor(numeric / 1_000_000);
  }

  if (numeric > 1_000_000_000_000) {
    return Math.floor(numeric);
  }

  if (numeric > 1_000_000_000) {
    return Math.floor(numeric * 1000);
  }

  return Date.now();
};

const deriveServiceName = (attributes: Record<string, unknown>, fallbackRoot?: Record<string, unknown>): string => {
  const nestedService = asObject(attributes.service);
  const serviceFromNested = asString(nestedService.name);

  if (serviceFromNested) {
    return serviceFromNested;
  }

  const serviceFromFlat = asString(attributes['service.name']);
  if (serviceFromFlat) {
    return serviceFromFlat;
  }

  if (fallbackRoot) {
    const serviceFromRoot = asString(fallbackRoot.serviceName ?? fallbackRoot.service_name ?? fallbackRoot.service);
    if (serviceFromRoot) {
      return serviceFromRoot;
    }
  }

  return 'unknown-service';
};

export const normalizeIncomingLog = (input: unknown): LogEntry => {
  const root = asObject(input);
  const attributes = {
    ...asObject(parseJsonIfString(root.attributesJson)),
    ...parseOtlpAttributes(root.attributes)
  };

  const severityNumber =
    asNumber(root.severityNumber) ??
    severityFromText(asString(root.severityText)) ??
    9;

  const body =
    asString(root.bodyText) ??
    bodyToText(root.body) ??
    asString(root.message) ??
    'Log event';

  const timestamp = timestampToMillis(
    root.timestamp ?? root.timeUnixNano ?? root.observedTimeUnixNano ?? root.observedTimestamp ?? root.ingestedAtMs
  );
  const traceId = asString(root.traceId);
  const spanId = asString(root.spanId);
  const serviceName = deriveServiceName(attributes, root);
  const rawId = asString(root.id);

  return {
    id: rawId ?? `${timestamp}-${Math.random().toString(36).slice(2, 10)}`,
    timestamp,
    severityNumber,
    severity: toSeverityKind(severityNumber),
    body,
    serviceName,
    traceId,
    spanId,
    attributes,
    raw: input
  };
};

const logsFromResourcePayload = (payload: Record<string, unknown>): LogEntry[] => {
  const resourceLogs = asArray(payload.resourceLogs);
  if (resourceLogs.length === 0) {
    return [];
  }

  const logs: LogEntry[] = [];

  resourceLogs.forEach((resourceLog) => {
    const resourceNode = asObject(resourceLog);
    const resourceAttributes = parseOtlpAttributes(asObject(resourceNode.resource).attributes);
    const resourceService = asString(resourceAttributes['service.name']);

    const scopeLogs = asArray(resourceNode.scopeLogs ?? resourceNode.instrumentationLibraryLogs);
    scopeLogs.forEach((scopeLog) => {
      const logRecords = asArray(asObject(scopeLog).logRecords);
      logRecords.forEach((record) => {
        const recordNode = asObject(record);
        const recordAttributes = parseOtlpAttributes(recordNode.attributes);
        if (resourceService && recordAttributes['service.name'] === undefined) {
          recordAttributes['service.name'] = resourceService;
        }

        logs.push(
          normalizeIncomingLog({
            id: recordNode.id,
            timestamp: recordNode.timeUnixNano ?? recordNode.observedTimeUnixNano ?? Date.now(),
            severityNumber: recordNode.severityNumber,
            severityText: recordNode.severityText,
            body: recordNode.body,
            traceId: recordNode.traceId,
            spanId: recordNode.spanId,
            attributes: recordAttributes,
            serviceName: resourceService
          })
        );
      });
    });
  });

  return logs;
};

export const extractLogsFromPayload = (payload: unknown): LogEntry[] => {
  if (Array.isArray(payload)) {
    return payload.map((entry) => normalizeIncomingLog(entry));
  }

  const root = asObject(payload);

  if (root.items && Array.isArray(root.items)) {
    return root.items.map((entry) => normalizeIncomingLog(entry));
  }

  const fromResource = logsFromResourcePayload(root);
  if (fromResource.length > 0) {
    return fromResource;
  }

  if (root.value !== undefined) {
    return extractLogsFromPayload(root.value);
  }

  if (root.log !== undefined) {
    return extractLogsFromPayload(root.log);
  }

  if (root.logs && Array.isArray(root.logs)) {
    return root.logs.map((entry) => normalizeIncomingLog(entry));
  }

  if (Object.keys(root).length > 0) {
    return [normalizeIncomingLog(root)];
  }

  return [];
};
